// Contacts (§3 /contacts + /contacts/{id}) — invoiceTest clients.js parity (S7).
// List: All/Customers/Suppliers segs (client-side over ONE type=all fetch) · search · Show disabled
//       · iT table (both balances, tags, tax/VAT, contact person, terms, View/Edit + Disable).
// Detail: card (name/acct/balance/phone) + actions + aging + Statement/Documents/Notes tabs + Edit.
import { html, signal } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { tenantShell } from './tshell.js';
import { paneFx } from '../shared/pane.js';
import { navigate } from '../shared/t4.js';
import { fmtR, formatDate, docCurrency, fmtIn, baseCurrency } from './bank_helpers.js';
import { t } from '../shared/i18n.js';
import { notice as notify } from '../shared/notice.js';
import { attachmentsCard } from '../shared/attachments.js';
import { renderHistoryAndNotes } from '../shared/historynotes.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import { searchSelect } from '../shared/searchselect.js';
import { backChip } from '../shared/vtnav.js';
import { rowDelete } from '../shared/confirm.js';
import { pager } from '../shared/pager.js';
import { SEG } from './approvals.js';   // type_code → route segment, for the statement Ref click-through
import { documentTypeLabel, statusClass, statusLabel } from './documents.js';   // shared document labels and status pills
import { companyRole, isCompanyAdmin } from '../shared/companyrole.js';

const money = v => html`<span class=${Number(v) < 0 ? 't-out' : (Number(v) > 0 ? 't-in' : '')}>${fmtR(v)}</span>`;

// ── List — invoiceTest clients.js layout (owner 2026-09-05 parity) ──────────────────────────────
// ONE type=all fetch; the All · Customers · Suppliers segs filter CLIENT-side on the flags (a
// contact that is both shows under Customers AND Suppliers — Lloyd's rule), search filters client-
// side too (iT), and each row shows BOTH balances (You owe / They owe). The drawer's Customers /
// Suppliers items land here pre-filtered via ?tab=. Schema differences kept deliberately: no
// billing_contact table (contact_person+email fill that column), no onboarding invites, and no
// hard delete — a contact with ledger history can only be DISABLED (the /toggle endpoint), so the
// iT Delete action reads Disable/Enable here.
const CONTACT_TABS = ['all', 'customers', 'suppliers'];
export function contactsScreen(companyId){
  const rows = signal([]), totals = signal({});
  const total = signal(0), page = signal(1), pages = signal(1);
  const filterTags = signal([]), tagFilter = signal('');
  const PER_PAGE = 20;
  let searchTimer = null, loadSeq = 0;
  const loading = signal(true), err = signal(''), listErr = signal('');
  const q = signal(''), showDisabled = signal(false);
  const seg = signal((() => {
    try { const v = new URLSearchParams(location.search).get('tab'); return CONTACT_TABS.includes(v) ? v : 'all'; }
    catch { return 'all'; }
  })());
  const armedId = signal(null), toggling = signal(false);

  const newOpen = signal(false), saving = signal(false), formErr = signal('');
  const nName = signal(''), nPerson = signal(''), nEmail = signal(''), nPhone = signal(''),
        nVat = signal(''), nTerms = signal('');
  // Sending an onboarding link to the new contact defaults to ON (owner Andre 2026-09-17:
  // "have a toggle which is on by default to send the onboarding link"). The toggle only
  // does something when there is an email address on the form — otherwise the checkbox
  // is a no-op on submit.
  const nSendOnboard = signal(true);
  // 0 = customer (debtor), 1 = supplier (creditor). Defaulted from the active tab when the form
  // opens (issue #62: the "all" tab silently created a customer with no way to tell) — but always
  // shown as an explicit choice in the form so the user knows, and can change it, what they create.
  const nType = signal(0);
  const openNew = () => {
    formErr.value = '';
    if(!newOpen.value) nType.value = seg.value === 'suppliers' ? 1 : 0;
    newOpen.value = !newOpen.value;
    if(newOpen.value) queueMicrotask(() => document.querySelector('.ct-new input:not([type="checkbox"])')?.focus());
  };
  // Always reads "+ Add Contact" regardless of the active tab (owner Andre 2026-09-17: "keep
  // this as Add Contact regardless of whether the Customers or Suppliers tabs are clicked").
  // The kind (customer vs supplier) is chosen inside the form itself.
  const addLabel = () => t('contacts.add_contact', 'Add Contact');
  // Clear the "A name is required" banner the moment the user types a name, rather than leaving it
  // up until the next Create. Mirrors the invoice editor's per-field clearInvalid (see CLAUDE.md).
  const clearNewErr = () => { if(formErr.value) formErr.value = ''; };

  async function load(){
    const requestedPage = page.value;
    const requestId = ++loadSeq;
    loading.value = true;
    const type = seg.value === 'customers' ? '0' : seg.value === 'suppliers' ? '1' : 'all';
    const p = new URLSearchParams({ type, page: String(requestedPage), per_page: String(PER_PAGE) });
    if(q.value.trim()) p.set('q', q.value.trim());
    if(tagFilter.value) p.set('tag_id', tagFilter.value);
    if(showDisabled.value) p.set('disabled', '1');
    try {
      const r = await api.get('/companies/' + companyId + '/contacts?' + p);
      if(requestId !== loadSeq) return;
      rows.value = r.contacts || []; totals.value = r.tab_counts || r.totals || {};
      total.value = Number(r.total ?? r.totals?.count ?? 0);
      pages.value = Number(r.pages || 1); page.value = Number(r.page || requestedPage); err.value = '';
    } catch(e){ err.value = (e.data && e.data.error) || t('contacts.err_load_list', 'Could not load contacts'); }
    if(requestId === loadSeq) loading.value = false;
  }
  load();

  async function loadFilterTags(){
    const type = seg.value === 'customers' ? '0' : seg.value === 'suppliers' ? '1' : 'all';
    try {
      const r = await api.get('/companies/' + companyId + '/tags?contact_type=' + type +
        (showDisabled.value ? '&disabled=1' : ''));
      filterTags.value = r.data || [];
      if(tagFilter.value && !filterTags.value.some(tag => String(tag.id) === String(tagFilter.value))) tagFilter.value = '';
    } catch { filterTags.value = []; }
  }
  loadFilterTags();

  const onSearch = e => {
    q.value = e.target.value; armedId.value = null; page.value = 1;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(load, 250);
  };
  const goPage = next => { page.value = next; armedId.value = null; load(); };

  async function createContact(e){
    e.preventDefault();
    if(!nName.value.trim()){ formErr.value = t('contacts.name_required', 'A name is required.'); return; }
    saving.value = true; formErr.value = '';
    try {
      const res = await api.post('/companies/' + companyId + '/contacts', {
        name: nName.value.trim(), type: nType.value,
        contact_person: nPerson.value.trim() || undefined, email: nEmail.value.trim() || undefined,
        phone: nPhone.value.trim() || undefined, vat_no: nVat.value.trim() || undefined,
        payment_terms_days: nTerms.value ? Number(nTerms.value) : undefined,
        // Ask the server to send an onboarding email when the toggle is on AND there is an
        // email address to send it to. The old flow never sent anything on create; this is
        // opt-out rather than opt-in (owner Andre 2026-09-17).
        send_onboarding: !!(nSendOnboard.value && nEmail.value.trim()) });
      nName.value = nPerson.value = nEmail.value = nPhone.value = nVat.value = nTerms.value = '';
      nSendOnboard.value = true;
      newOpen.value = false;
      await load();
      if(res && res.id) navigate('/co/' + companyId + '/contacts/' + res.id);
    } catch(e2){ formErr.value = (e2.data && e2.data.error) || e2.message || t('contacts.create_failed', 'Could not create the contact'); }
    saving.value = false;
  }

  async function doToggle(c){
    toggling.value = true; listErr.value = '';
    try {
      // the endpoint SETS the flag ({is_active}), it does not flip it — send the inverse explicitly
      await api.post('/companies/' + companyId + '/contacts/' + c.id + '/toggle', { is_active: !c.is_active });
      armedId.value = null;
      await load();
    } catch(e){ listErr.value = (e.data && e.data.error) || t('contacts.toggle_failed', 'Could not change the contact'); }
    toggling.value = false;
  }

  const list = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const all = rows.value, armed = armedId.value, busy = toggling.value;
    const needle = q.value.trim().toLowerCase();
    const body = all;
    if(!body.length){
      return html`<div class="empty muted-block">${needle
        ? t('contacts.none_match', 'No contacts match') + ' “' + q.value.trim() + '”.'
        : t('contacts.none_yet', 'No contacts yet — click + Add Contact.')}</div>`;
    }
    // ONE Balance column (owner 2026-09-17: "i'm interested in their balance — you can tag
    // you owe / they owe depending on creditor or debtor"). Customer → 'They owe' is the
    // primary side; Supplier → 'You owe'. When only one side has a balance, the label reads
    // that side; both sides > 0 (a contact that's both customer AND supplier) shows both on
    // separate lines. Tax No / VAT No are gone entirely — they live on the detail screen.
    const balanceCell = c => {
      const yo = Number(c.you_owe || 0), th = Number(c.they_owe || 0);
      if(!yo && !th) return html`<span class="muted">—</span>`;
      const you = yo ? html`<div class="ct-bal-line ct-bal-us">${t('contacts.you_owe', 'You owe')} <b>${money(yo)}</b></div>` : '';
      const them = th ? html`<div class="ct-bal-line ct-bal-them">${t('contacts.they_owe', 'They owe')} <b>${money(th)}</b></div>` : '';
      // Show the more relevant side first based on contact type
      return c.is_supplier && !c.is_customer ? html`${you}${them}` : html`${them}${you}`;
    };

    // Payment terms + Contact person columns dropped from the list (owner 2026-09-17: "I
    // don't need to see terms either"; contact person moves with them since it belongs on
    // the detail screen). List now shows just Contact / Balance / Actions.
    return html`<div class="tbl-wrap"><table class="list ct-list">
      <thead><tr><th>${t('contacts.contact', 'Contact')}</th>
        <th>${t('contacts.balance', 'Balance')}</th>
        <th class="ta-r">${t('common.actions', 'Actions')}</th></tr></thead>
      <tbody>${body.map(c => html`<tr class=${'ct-row-link' + (c.is_active ? '' : ' ct-off')}
          @click=${() => navigate('/co/' + companyId + '/contacts/' + c.id)}
          title=${t('contacts.open_contact', 'Open contact')}>
        <td><b class="ct-name-link">${c.name}</b>
          <div class="ct-sub-row">
            <span class="contact-tags">
              ${c.is_customer ? html`<button type="button" class="tag-cust tag-link"
                @click=${() => navigate('/co/' + companyId + '/contacts/' + c.id)}
                title=${t('contacts.open_customer', 'Open customer')}>${t('contacts.customer', 'Customer')}</button>` : ''}
              ${c.is_supplier ? html`<button type="button" class="tag-supp tag-link"
                @click=${() => navigate('/co/' + companyId + '/contacts/' + c.id)}
                title=${t('contacts.open_supplier', 'Open supplier')}>${t('contacts.supplier', 'Supplier')}</button>` : ''}
              ${c.is_active ? '' : html`<span class="tag-off">${t('contacts.disabled', 'disabled')}</span>`}</span>
            <small class="cell-sub mono">${c.code}</small>
          </div>
          <!-- Mobile-only tap actions: dial the phone / open the email app. CSS hides these
               on desktop; they're the primary "action" on a phone-in-hand list -->
          <div class="ct-quick-actions">
            ${c.phone ? html`<a class="ct-quick ct-quick-call" href=${'tel:' + c.phone}
              @click=${e => e.stopPropagation()} aria-label=${t('contacts.call', 'Call')}>${icon('phone', {size: 14})} ${c.phone}</a>` : ''}
            ${c.email ? html`<a class="ct-quick ct-quick-mail" href=${'mailto:' + c.email}
              @click=${e => e.stopPropagation()} aria-label=${t('contacts.email', 'Email')}>${icon('mail', {size: 14})} ${c.email}</a>` : ''}
          </div>
        </td>
        <td class="ct-bal">${balanceCell(c)}</td>
        <td class="ta-r nowrap" @click=${e => e.stopPropagation()}>
          <button class="icon-btn" title=${t('contacts.edit', 'Edit contact')}
            @click=${() => navigate('/co/' + companyId + '/contacts/' + c.id + '?edit=1')}>${icon('pencil', {size: 14})}</button>
          ${armed === c.id
            ? html`<button class="btn sm bad" ?disabled=${busy} @click=${() => doToggle(c)}>${busy ? '…' : t('doc.confirm', 'Confirm')}</button><button class="btn sm" @click=${() => { armedId.value = null; }}>${t('common.cancel', 'Cancel')}</button>`
            : html`<button class="icon-btn" title=${c.is_active ? t('contacts.disable', 'Disable') : t('contacts.enable', 'Enable')}
                @click=${() => { armedId.value = c.id; listErr.value = ''; }}>${icon(c.is_active ? 'ban' : 'rotate-ccw', {size: 14})}</button>`}
        </td></tr>`)}</tbody>
    </table></div>`;
  };

  const segsRow = () => {
    const cur = seg.value;
    const tabBtn = (key, label) => html`<button type="button" class=${'seg' + (cur === key ? ' on' : '')}
      @click=${async () => { seg.value = key; tagFilter.value = ''; page.value = 1; armedId.value = null; await loadFilterTags(); load(); }}>${label}</button>`;
    return html`<div class="segs">${tabBtn('all', t('common.all', 'All'))}${tabBtn('customers', t('contacts.customers', 'Customers'))}${tabBtn('suppliers', t('contacts.suppliers', 'Suppliers'))}</div>`;
  };

  const contactCountLabel = () => {
    const count = total.value || 0;
    return count + ' ' + (count === 1 ? t('contacts.contact', 'contact') : t('common.contacts', 'contacts'));
  };
  const tagPills = () => filterTags.value.length ? html`<div class="segs ct-tag-filters" aria-label="Contact tags">
    <button type="button" class=${'seg' + (!tagFilter.value ? ' on' : '')}
      @click=${() => { tagFilter.value = ''; page.value = 1; armedId.value = null; load(); }}>${t('common.all', 'All')}</button>
    ${filterTags.value.map(tag => html`<button type="button" class=${'seg' + (String(tagFilter.value) === String(tag.id) ? ' on' : '')}
      @click=${() => { tagFilter.value = String(tag.id); page.value = 1; armedId.value = null; load(); }}>${tag.label} <span class="seg-n">${tag.contact_count}</span></button>`)}
  </div>` : '';

  const content = html`<div class="page page-wide ct-page ux-list-page">
    <h1 class="sr-only">${() => t('common.contacts', 'Contacts')}</h1>

    <div class="ux-list-actions">
      <input class="search ux-search doc-search-inline" type="search" .value=${q} @input=${onSearch}
        placeholder=${t('contacts.search_ph2', 'e.g. Blue Sky')} aria-label=${t('contacts.search_aria', 'Search for a contact')}>
      <button class="btn primary doc-newbtn" @click=${openNew}>+ ${() => addLabel()}</button>
    </div>
    <div class="muted ct-count list-count">${contactCountLabel}</div>

    <div class="doc-tabhead">
      ${segsRow}
      ${tagPills}
    </div>

    <div class="list-toolbar">
      <label class="switch"><input type="checkbox" .checked=${showDisabled}
        @change=${async e => { showDisabled.value = e.target.checked; page.value = 1; await loadFilterTags(); load(); }}><span class="switch-track"></span> ${() => t('contacts.show_disabled', 'Show disabled')}</label>
    </div>

    ${() => listErr.value ? html`<div class="alert">${listErr.value}</div>` : ''}

    ${() => newOpen.value ? html`<form class="card mb-2 ct-new" @submit=${createContact}>
      <div class="card-body">
        <div class="ct-type-choose">
          <span class="ct-type-lbl">${t('contacts.creating_a', 'Creating a')}</span>
          <div class="segs ct-type-segs" role="group" aria-label=${t('contacts.contact_kind', 'Contact kind')}>
            <button type="button" class=${() => 'seg' + (nType.value === 0 ? ' on' : '')} @click=${() => { nType.value = 0; }}>${t('contacts.customer', 'Customer')}</button>
            <button type="button" class=${() => 'seg' + (nType.value === 1 ? ' on' : '')} @click=${() => { nType.value = 1; }}>${t('contacts.supplier', 'Supplier')}</button>
          </div>
          <small class="ct-type-hint">${() => nType.value === 1
            ? t('contacts.creating_supplier_hint', 'A supplier you buy from (creditor).')
            : t('contacts.creating_customer_hint', 'A customer you sell to (debtor).')}</small>
        </div>
        <div class="ct-new-grid">
          <!-- Owner (2026-09-17): keep the quick-create lean — Name, Contact person, Email,
               Phone. VAT number + Terms (days) move to the full contact edit screen. -->
          <input class="form-control" .value=${nName} @input=${e => { clearNewErr(); nName.value = e.target.value; }} placeholder=${t('common.name', 'Name')} aria-label=${t('common.name', 'Name')}>
          <input class="form-control" .value=${nPerson} @input=${e => { nPerson.value = e.target.value; }} placeholder=${t('contacts.contact_person', 'Contact person')} aria-label=${t('contacts.contact_person', 'Contact person')}>
          <input class="form-control" type="email" .value=${nEmail} @input=${e => { nEmail.value = e.target.value; }} placeholder=${t('common.email', 'Email')} aria-label=${t('common.email', 'Email')}>
          <input class="form-control" .value=${nPhone} @input=${e => { nPhone.value = e.target.value; }} placeholder=${t('contacts.phone', 'Phone')} aria-label=${t('contacts.phone', 'Phone')}>
        </div>
        <label class="switch ct-onboard-toggle">
          <input type="checkbox" .checked=${nSendOnboard} @change=${e => { nSendOnboard.value = e.target.checked; }}>
          <span class="switch-track"></span>
          <span>${t('contacts.send_onboarding', 'Send onboarding link')}</span>
          <small class="muted">${() => nEmail.value.trim() ? t('contacts.send_onboarding_sub', 'They get a link to their portal.') : t('contacts.send_onboarding_no_email', 'Needs an email above.')}</small>
        </label>
        <div class="ct-new-actions">
          ${() => formErr.value ? html`<div class="row-err">${formErr}</div>` : ''}
          <span class="jrn-spacer"></span>
          <button type="button" class="btn btn-outline" @click=${() => { newOpen.value = false; }}>${t('common.cancel', 'Cancel')}</button>
          <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? t('common.creating', 'Creating…') : t('contacts.create_contact', 'Create contact')}</button>
        </div>
      </div>
    </form>` : ''}

    ${list}
    ${() => pager({ page: page.value, totalPages: pages.value, total: total.value,
      perPage: PER_PAGE, onPage: goPage })}
  </div>`;

  return tenantShell(companyId, t('common.contacts', 'Contacts'), content);
}

// ── Detail + Edit ─────────────────────────────────────────────────────────────
export function contactDetailScreen(companyId, contactId){
  // Emailing a statement / onboarding link out to a contact is an admin/owner action (owner
  // 2026-09-11); the server enforces it and the client hides the send buttons from a USER.
  // isCompanyAdmin(null) is false, so the safe member view shows until /me resolves.
  const coRole = companyRole(companyId);
  const data = signal(null), loading = signal(true), err = signal('');
  const tab = signal('statement');
  const dpane = paneFx();   // tab-pane switch effect
  // Open straight into edit mode when the list's pencil action linked here with ?edit=1 (owner Andre
  // 2026-09-18: "Edit Contact should go straight to opening the Edit" — from the contacts landing).
  const editing = signal(new URLSearchParams(location.search).get('edit') === '1');
  const saving = signal(false), editErr = signal('');
  const newMenuOpen = signal(false);   // the "New ▾" document dropdown in the action row
  // Clear the "A name is required" banner as the user edits a field, rather than leaving it up until
  // the next Save. Mirrors the invoice editor's per-field clearInvalid (see CLAUDE.md).
  const clearEditErr = () => { if(editErr.value) editErr.value = ''; };
  const reference = signal(null);
  const f = {};   // edit-form field signals, populated on load
  const FIELDS = ['code', 'name', 'contact_person', 'email', 'phone', 'mobile', 'website_url',
    'vat_no', 'tax_no', 'reg_no',
    // Bank details (migration 130) — the record a batch debit-order run reads. SA/local fields
    // grouped first; SWIFT/IBAN are the international pair. All round-trip through update_contact.
    'bank_account_name', 'bank_name', 'bank_account_no', 'bank_branch_code', 'bank_account_type',
    'bank_swift', 'bank_iban'];
  FIELDS.forEach(k => f[k] = signal(''));
  const fDisabled = signal(false);
  // The SWIFT/IBAN pair is for international banking — hidden behind a toggle so a South African
  // contact sees a clean local form, revealed automatically when the contact already has one on
  // file (owner 2026-09-19: "other countries need SWIFT and/or IBAN").
  const bankIntlOpen = signal(false);
  // Addresses (many per contact, migration 124) — managed inline in the edit form via
  // /contacts/{id}/addresses. The flat single address that used to live on the contact is gone.
  const addresses = signal([]);
  const addrOpen = signal(false), addrEditId = signal(null), addrBusy = signal(false), addrErr = signal('');
  const addrDelId = signal(null), addrDelBusy = signal(false), addrDelErr = signal('');
  const aFld = {};
  const A_FIELDS = ['label', 'attention', 'line1', 'line2', 'town', 'province', 'postal_code', 'phone', 'notes'];
  A_FIELDS.forEach(k => aFld[k] = signal(''));
  const aCountry = signal(''), aBilling = signal(false), aDelivery = signal(false);
  // #25 (Andre 2026-09-14, multi-currency): a contact can be billed in a currency other than the
  // company base, and carries its own country. Both round-trip through update_contact; '' = base /
  // unspecified. The active currency + country lists ride the /bank-reference bundle.
  const fCurrency = signal(''), fCountry = signal('');

  // Note posting now lives entirely in the History-and-notes section (historynotes.js POSTs to
  // the same /contacts/{id}/notes route), so the old inline compose signals + handlers here are
  // gone (owner Andre 2026-09-17: "get rid of unneeded add note", removed the Notes tab).
  const cTags = signal([]), newTag = signal(''), tagBusy = signal(false);
  // Every tag the company already uses — feeds the tag input's autocomplete so typing a tag
  // suggests existing ones (pick one or type a new label). Loaded once on mount, refreshed after
  // a tag is created so a brand-new label becomes a future suggestion.
  const companyTags = signal([]);
  async function loadCompanyTags(){
    try { const r = await api.get('/companies/' + companyId + '/tags'); companyTags.value = r.data || []; }
    catch { /* keep last — autocomplete is a convenience, never blocks tagging */ }
  }
  // attachments (Wave C1) — one card instance per screen mount; called PLAINLY inside body()
  const attachmentsView = attachmentsCard({
    base: '/companies/' + companyId + '/contacts/' + contactId,
    attachmentBase: '/companies/' + companyId + '/contact-attachments',
    title: t('contacts.attachments', 'Attachments'),
  });
  // History & notes (Xero-style) — a STABLE node built once and interpolated by reference at the
  // foot of the detail (same mounting contract as document_detail.js). Reads the contact's derived
  // activity stream (Contact created + notes/tasks + its documents' lifecycle events); "Add note"
  // posts to the existing /contacts/{id}/notes route.
  const historyNotesNode = renderHistoryAndNotes({
    base: '/companies/' + companyId + '/contacts/' + contactId,
    followUp: true,   // contact notes offer an optional follow-up (reminder) date
  });
  // "Add note" is a single button in the header action row (owner Andre 2026-09-17: "Move
  // down to before Email Statement — make same style as those buttons" + "get rid of
  // unneeded add note"). The historynotes.js section rebuilds its head on every refresh,
  // so we hide ITS own Add-note button with a persistent class + CSS rule instead of a
  // one-shot inline style. The header button still delegates through the same [data-addnote]
  // click so historynotes.js remains the single source of truth for the compose flow.
  historyNotesNode.classList.add('hn-hide-addnote');
  function openHistoryAddNote(){
    const btn = historyNotesNode.querySelector('[data-addnote]');
    if(!btn) return;
    btn.click();
    historyNotesNode.scrollIntoView({behavior: 'smooth', block: 'center'});
  }

  // ── Addresses (migration 124) ────────────────────────────────────────────────
  const addrBase = '/companies/' + companyId + '/contacts/' + contactId + '/addresses';
  async function loadAddresses(){
    try { const r = await api.get(addrBase); addresses.value = r.data || []; } catch { /* keep last */ }
  }
  function resetAddrForm(){
    A_FIELDS.forEach(k => aFld[k].value = '');
    aCountry.value = fCountry.value || '';   // seed from the contact's country
    aBilling.value = false; aDelivery.value = false; addrErr.value = '';
  }
  function openAddrNew(){
    addrEditId.value = null; resetAddrForm();
    // The first address becomes both defaults server-side; reflect that in the form.
    if(!addresses.value.length){ aBilling.value = true; aDelivery.value = true; }
    addrOpen.value = true;
  }
  function openAddrEdit(a){
    addrEditId.value = a.id;
    A_FIELDS.forEach(k => aFld[k].value = a[k] == null ? '' : String(a[k]));
    aCountry.value = a.country_id ? String(a.country_id) : '';
    aBilling.value = !!a.is_default_billing; aDelivery.value = !!a.is_default_delivery;
    addrErr.value = ''; addrOpen.value = true;
  }
  function closeAddr(){ addrOpen.value = false; }
  async function saveAddr(e){
    if(e) e.preventDefault();
    const body = { is_default_billing: aBilling.value, is_default_delivery: aDelivery.value,
      country_id: aCountry.value ? Number(aCountry.value) : null };
    A_FIELDS.forEach(k => { body[k] = aFld[k].value.trim() || null; });
    if(!body.line1 && !body.town && !body.postal_code){
      addrErr.value = t('contacts.addr_need_something', 'Enter at least a street, town or postal code'); return;
    }
    addrBusy.value = true; addrErr.value = '';
    try {
      if(addrEditId.value) await api.put(addrBase + '/' + addrEditId.value, body);
      else                 await api.post(addrBase, body);
      await loadAddresses();
      addrOpen.value = false;
    } catch(e2){ addrErr.value = (e2.data && e2.data.error) || t('contacts.addr_save_failed', 'Could not save the address'); }
    addrBusy.value = false;
  }
  function armAddrDelete(a){ addrDelErr.value = ''; addrDelId.value = a.id; }
  function cancelAddrDelete(){ addrDelId.value = null; addrDelErr.value = ''; }
  async function delAddr(a){
    addrDelBusy.value = true; addrDelErr.value = '';
    try { await api.post(addrBase + '/' + a.id + '/delete', {}); addrDelId.value = null; await loadAddresses(); }
    catch(e){ addrDelErr.value = (e.data && e.data.error) || t('contacts.addr_del_failed', 'Could not remove the address'); }
    addrDelBusy.value = false;
  }
  async function setAddrDefault(a, kind){
    const body = kind === 'billing' ? { is_default_billing: true } : { is_default_delivery: true };
    try { await api.put(addrBase + '/' + a.id, body); await loadAddresses(); }
    catch(e){ toast((e.data && e.data.error) || t('contacts.addr_default_failed', 'Could not set the default'), 'bad'); }
  }

  const dmy = iso => formatDate(iso, (reference.value || {}).date_format);

  async function load(){
    loading.value = true;
    try {
      const r = await api.get('/companies/' + companyId + '/contacts/' + contactId);
      data.value = r;
      cTags.value = r.tags || [];
      const c = r.contact || {};
      FIELDS.forEach(k => f[k].value = c[k] == null ? '' : String(c[k]));
      fDisabled.value = !c.is_active;
      bankIntlOpen.value = !!(c.bank_swift || c.bank_iban);
      // Billing currency defaults to the company base currency when the contact has none set
      // (owner Andre 2026-09-17: "The default should be the base currency of the company").
      // Reference bundle carries base_currency_id — falls back to '' if reference hasn't loaded
      // yet; the reference loader below re-applies the base once it arrives.
      fCurrency.value = c.preferred_currency_id
        ? String(c.preferred_currency_id)
        : String((reference.value || {}).base_currency_id || '');
      // Country defaults to the company base country when the contact has none set (owner Andre
      // 2026-09-18). base_country_id rides the reference bundle; the loader below re-applies it if
      // reference lands after the contact.
      fCountry.value = c.country_id
        ? String(c.country_id)
        : String((reference.value || {}).base_country_id || '');
      addresses.value = r.addresses || [];
      err.value = '';
    } catch(e){ err.value = (e.data && e.data.error) || t('contacts.err_load_detail', 'Could not load the contact'); }
    loading.value = false;
  }
  load();
  loadCompanyTags();
  (async () => {
    try {
      reference.value = await api.get('/companies/' + companyId + '/bank-reference');
      // Feed the company date_format (+ periods) to <sleek-date> so the follow-up field in
      // History-and-notes formats per the global settings, not the browser locale (owner Andre
      // 2026-09-17: "the date format … must come from the global settings … use the components").
      configureSleekDate(reference.value);
      // If the contact loaded before reference and had no currency/country, fill in the company
      // base now that we know it (owner Andre 2026-09-17/18: base currency + base country default).
      const c = (data.value || {}).contact || {};
      if(!c.preferred_currency_id && !fCurrency.value && reference.value?.base_currency_id){
        fCurrency.value = String(reference.value.base_currency_id);
      }
      if(!c.country_id && !fCountry.value && reference.value?.base_country_id){
        fCountry.value = String(reference.value.base_country_id);
      }
    } catch {}
  })();

  // Positive actions are green, failures red — via the shared floating toast (correct on every
  // brand), instead of the old single hard-coded green box that painted errors green too.
  function toast(m, kind){ notify(m, kind || 'ok'); }

  async function save(e){
    e.preventDefault();
    if(!f.name.value.trim()){ editErr.value = t('contacts.name_required', 'A name is required.'); return; }
    saving.value = true; editErr.value = '';
    const body = { is_active: !fDisabled.value };
    FIELDS.forEach(k => { body[k] = f[k].value.trim(); });
    body.preferred_currency_id = fCurrency.value ? Number(fCurrency.value) : null;
    body.country_id = fCountry.value ? Number(fCountry.value) : null;
    try {
      await api.put('/companies/' + companyId + '/contacts/' + contactId, body);
      editing.value = false; await load(); toast(t('contacts.saved', 'Contact saved'));
    } catch(e2){ editErr.value = (e2.data && e2.data.error) || e2.message || t('contacts.save_failed', 'Could not save'); }
    saving.value = false;
  }

  async function addTag(e){
    if(e) e.preventDefault();
    const label = newTag.value.trim();
    if(!label || tagBusy.value) return;
    tagBusy.value = true;
    try {
      const r = await api.post('/companies/' + companyId + '/contacts/' + contactId + '/tags', { label });
      cTags.value = r.tags || []; newTag.value = '';
      loadCompanyTags();   // a new label becomes a suggestion for the next contact
    } catch(e2){ toast((e2.data && e2.data.error) || t('contacts.tag_add_failed', 'Could not add the tag'), 'bad'); }
    tagBusy.value = false;
  }
  async function removeTag(tagId){
    if(tagBusy.value) return; tagBusy.value = true;
    try {
      const r = await api.post('/companies/' + companyId + '/contacts/' + contactId + '/tags/' + tagId + '/remove', {});
      cTags.value = r.tags || [];
    } catch(e){ toast((e.data && e.data.error) || t('contacts.tag_remove_failed', 'Could not remove the tag'), 'bad'); }
    tagBusy.value = false;
  }

  // ── Onboard (v2 generate-link / email-portal-link) ────────────────────────────
  const onboardOpen = signal(false), onboardBusy = signal(false), onboardLink = signal('');
  let onboardEmail = '';
  async function generateOnboardLink(){
    onboardBusy.value = true;
    try {
      const r = await api.post('/companies/' + companyId + '/contacts/' + contactId + '/generate-link', { expires_days: 7 });
      onboardLink.value = window.location.origin + '/portal/' + r.token;
    } catch(e){ toast((e.data && e.data.error) || t('contacts.link_gen_failed', 'Could not generate the link'), 'bad'); }
    onboardBusy.value = false;
  }
  function copyOnboardLink(){
    try { navigator.clipboard.writeText(onboardLink.value); toast(t('contacts.link_copied', 'Link copied')); }
    catch { toast(t('contacts.copy_failed', 'Could not copy — select and copy it by hand'), 'bad'); }
  }
  async function emailOnboardLink(){
    const to = (onboardEmail || '').trim();
    if(!to || !to.includes('@')){ toast(t('contacts.valid_email_required', 'Enter a valid email address'), 'bad'); return; }
    onboardBusy.value = true;
    try {
      const r = await api.post('/companies/' + companyId + '/contacts/' + contactId + '/email-portal-link',
        { email: to, base_url: window.location.origin, expires_days: 7 });
      onboardLink.value = r.url || onboardLink.value;
      toast(r.message || t('contacts.link_emailed', 'Onboarding link emailed'));
    } catch(e){ toast((e.data && e.data.error) || t('contacts.link_email_failed', 'Could not email the link'), 'bad'); }
    onboardBusy.value = false;
  }

  const stmtBusy = signal(false);
  async function emailStatement(){
    if(stmtBusy.value) return;
    stmtBusy.value = true;
    try {
      const r = await api.post('/companies/' + companyId + '/contacts/' + contactId + '/email-statement', {});
      toast(r.message || t('contacts.statement_emailed', 'Statement emailed'));
      await load();                        // the Communications tab gains the outbox row
    } catch(e){ toast((e.data && e.data.error) || t('contacts.stmt_email_failed', 'Could not email the statement'), 'bad'); }
    stmtBusy.value = false;
  }

  // C6: the printable account statement (transactions + aging) — same data as the Statement tab.
  const stmtPdfBusy = signal(false);
  // X2b: mute/unmute automatic reminders for EVERY invoice of this contact (manual send unaffected).
  const ctMuteBusy = signal(false);
  async function toggleReminderMute(){
    if(ctMuteBusy.value || !data.value || !data.value.contact) return;
    const next = !data.value.contact.reminders_muted;
    ctMuteBusy.value = true;
    try {
      await api.post('/companies/' + companyId + '/contacts/' + contactId + '/reminders-mute', { muted: next });
      await load();
      toast(next ? t('contacts.reminders_muted_notice', 'Automatic reminders muted for this contact — every invoice of theirs is skipped.')
                 : t('contacts.reminders_unmuted_notice', 'Automatic reminders back on for this contact.'));
    } catch(e){ toast((e.data && e.data.error) || t('contacts.mute_failed', 'Could not change the reminder setting'), 'bad'); }
    ctMuteBusy.value = false;
  }
  async function downloadStatementPdf(){
    if(stmtPdfBusy.value) return;
    stmtPdfBusy.value = true;
    try { await api.download('/companies/' + companyId + '/contacts/' + contactId + '/statement/pdf', 'statement.pdf'); }
    catch(e){ toast((e.data && e.data.error) || t('contacts.stmt_pdf_failed', 'Could not render the statement'), 'bad'); }
    stmtPdfBusy.value = false;
  }


  // Apply an existing on-account credit to invoices (Wave D2) — plain-DOM modal, same focus-safe
  // pattern. Pure allocation, no bank/amount; the chosen credit's available balance caps it.
  function _autofill(allocEls, cap){
    let left = Math.round(cap * 100) / 100;
    allocEls.forEach(el => {
      const out = Number(el.dataset.out) || 0;
      const take = Math.max(0, Math.min(out, left));
      el.value = take ? take.toFixed(2) : '0';
      left = Math.round((left - take) * 100) / 100;
    });
  }
  async function openApplyCreditModal(){
    let od;
    try { od = await api.get('/companies/' + companyId + '/contacts/' + contactId + '/open-items'); }
    catch(e){ toast((e.data && e.data.error) || t('contacts.receive_load_failed', 'Could not load open items'), 'bad'); return; }
    const invoices = (od && od.invoices) || [];
    const credits = (od && od.credits) || [];
    if(!credits.length){ toast(t('contacts.no_credits', 'No on-account credit to apply'), 'bad'); return; }
    if(!invoices.length){ toast(t('contacts.no_open_invoices_short', 'No open invoices to apply a credit to'), 'bad'); return; }
    const avail = oi => Math.round(-(oi.outstanding || 0) * 100) / 100;   // credit outstanding is negative
    const node = html`<div class="modal-overlay">
      <div class="modal-card modal-wide">
        <div class="modal-head"><h3>${t('contacts.apply_credit', 'Apply a credit')}</h3>
          <button type="button" class="modal-x" data-ac-cancel>✕</button></div>
        <p class="form-note">${t('contacts.apply_credit_hint', 'Apply on-account credit to open invoices. No money moves — this only allocates an existing credit.')}</p>
        <label class="pay-f"><span>${t('contacts.credit', 'Credit')}</span>
          <span data-ac-credit></span></label>
        <table class="doc-lines-tbl rc-alloc">
          <thead><tr><th>${t('doc.col_number', 'Number')}</th><th>${t('common.date', 'Date')}</th><th class="r">${t('doc.outstanding', 'Outstanding')}</th><th class="r">${t('contacts.apply', 'Apply')}</th></tr></thead>
          <tbody>${invoices.map(iv => html`<tr>
            <td class="mono">${iv.document_no}</td><td class="mono">${dmy(iv.date)}</td>
            <td class="r mono">${fmtR(iv.outstanding)}</td>
            <td class="r"><input class="form-control rc-alloc-in" type="number" step="0.01" data-alloc data-oi=${iv.open_item_id} data-out=${iv.outstanding}></td></tr>`)}</tbody>
        </table>
        <div class="rc-summary">
          <span>${t('contacts.applied', 'Applied')}: <b data-ac-applied>—</b></span>
          <span>${t('contacts.credit_remaining', 'Credit remaining')}: <b data-ac-remain>—</b></span>
        </div>
        <div class="row-err" data-ac-err hidden></div>
        <div class="pay-actions">
          <button type="button" class="btn btn-outline" data-ac-cancel2>${t('common.cancel', 'Cancel')}</button>
          <button type="button" class="btn btn-primary" data-ac-go>${t('contacts.apply_credit_go', 'Apply credit')}</button>
        </div>
      </div></div>`;
    const overlay = node.querySelector('.modal-overlay');
    const appliedLbl = node.querySelector('[data-ac-applied]');
    const remainLbl = node.querySelector('[data-ac-remain]');
    const errEl = node.querySelector('[data-ac-err]');
    const goEl = node.querySelector('[data-ac-go]');
    const allocEls = [...node.querySelectorAll('[data-alloc]')];
    // The credit picker is an iT searchSelect (S14); each credit's available balance rides this
    // Map (was the native option's data-avail) so selAvail survives the control swap.
    const availBy = new Map(credits.map(cr => [String(cr.open_item_id), avail(cr)]));
    const creditSel = searchSelect({
      options: credits.map(cr => ({ value: String(cr.open_item_id),
        label: (cr.document_no || t('contacts.on_account_credit', 'On-account credit')) + ' · ' + fmtR(avail(cr)) })),
      value: String(credits[0].open_item_id),
      placeholder: t('contacts.select_credit', '— Select credit —'),
      ariaLabel: t('contacts.credit', 'Credit'),
      onSelect: () => { _autofill(allocEls, selAvail()); recompute(); },
    });
    node.querySelector('[data-ac-credit]').append(creditSel.el);
    const selAvail = () => availBy.get(creditSel.get()) || 0;
    function recompute(){
      let applied = 0;
      const cap = selAvail();
      allocEls.forEach(el => {
        let v = Number(el.value) || 0;
        const out = Number(el.dataset.out) || 0;
        if(v < 0){ v = 0; el.value = '0'; }
        if(v > out + 0.005){ v = out; el.value = out.toFixed(2); }
        applied += v;
      });
      applied = Math.round(applied * 100) / 100;
      appliedLbl.textContent = fmtR(applied);
      const remain = Math.round((cap - applied) * 100) / 100;
      remainLbl.textContent = fmtR(remain);
      remainLbl.classList.toggle('rc-bad', remain < -0.005);
    }
    _autofill(allocEls, selAvail());
    recompute();
    allocEls.forEach(el => el.addEventListener('input', recompute));
    const close = () => overlay.remove();
    node.querySelector('[data-ac-cancel]').addEventListener('click', close);
    node.querySelector('[data-ac-cancel2]').addEventListener('click', close);
    goEl.addEventListener('click', async () => {
      errEl.hidden = true;
      const allocations = allocEls.map(el => ({ open_item_id: Number(el.dataset.oi), amount: Number(el.value) || 0 }))
        .filter(a => a.amount > 0);
      if(!allocations.length){ errEl.hidden = false; errEl.textContent = t('contacts.pick_invoice', 'Allocate the credit to at least one invoice.'); return; }
      const applied = Math.round(allocations.reduce((s, a) => s + a.amount, 0) * 100) / 100;
      if(applied > selAvail() + 0.005){ errEl.hidden = false; errEl.textContent = t('contacts.over_credit', 'You have applied more than the credit available.'); return; }
      goEl.disabled = true; goEl.textContent = t('common.saving', 'Saving…');
      try {
        await api.post('/companies/' + companyId + '/contacts/' + contactId + '/apply-credit', {
          credit_open_item_id: Number(creditSel.get()), allocations });
        close(); await load(); toast(t('contacts.credit_applied', 'Credit applied'));
      } catch(e){ goEl.disabled = false; goEl.textContent = t('contacts.apply_credit_go', 'Apply credit');
        errEl.hidden = false; errEl.textContent = (e.data && e.data.error) || t('contacts.apply_failed', 'Could not apply the credit'); }
    });
    document.body.appendChild(node);
  }

  const detailAging = (aging) => {
    const cards = [[t('common.current', 'Current'), aging.d_current], [t('common.30_days', '30 days'), aging.d30], [t('common.60_days', '60 days'), aging.d60],
                   [t('common.90_days', '90 days'), aging.d90], [t('contacts.120_days_plus', '120 days +'), aging.d120]];
    return html`<div class="aging-cards">${cards.map(([lbl, v]) => html`
      <div class="aging-card"><div class="aging-lbl">${lbl}</div><div class="aging-val">${money(v || 0)}</div></div>`)}</div>`;
  };

  const editForm = (c) => html`<form class="card mb-2" @submit=${save}>
    <div class="card-body ct-edit">
      <div class="ct-edit-sec">${t('contacts.general', 'General')}</div>
      <div class="ct-edit-grid ct-edit-grid-2">
        ${field('name', t('common.name', 'Name'), t('contacts.eg_name', 'e.g. Blue Widgets (Pty) Ltd'))}
        ${field('code', t('contacts.account_number', 'Account number'), t('contacts.eg_code', 'e.g. D00012'))}
        ${field('phone', t('contacts.telephone', 'Telephone'), t('contacts.eg_phone', 'e.g. 011 000 0000'))}
        ${field('mobile', t('contacts.cellphone', 'Cellphone'), t('contacts.eg_mobile', 'e.g. 082 000 0000'))}
        ${field('contact_person', t('contacts.contact_person', 'Contact person'), t('contacts.eg_person', 'e.g. Jane Doe'))}
        ${field('email', t('common.email', 'Email'), t('contacts.eg_email', 'e.g. pay@blue.co.za'))}
        ${field('website_url', t('contacts.website', 'Website'), t('contacts.eg_website', 'e.g. www.blue.co.za'), 'text', 'ct-field-full')}
      </div>
      <label class="switch ct-edit-disabled"><input type="checkbox" .checked=${fDisabled}
        @change=${e => { fDisabled.value = e.target.checked; }}><span class="switch-track"></span> ${t('contacts.disabled', 'Disabled')}</label>

      <div class="ct-edit-sec ct-addr-sec">
        <span>${t('contacts.addresses', 'Addresses')}</span>
        <button type="button" class="btn btn-sm btn-outline ct-addr-add" @click=${openAddrNew}>${icon('plus', {size: 13})} ${t('contacts.add_address', 'Add address')}</button>
      </div>
      ${addressManager()}

      <div class="ct-edit-sec">${t('contacts.currency_country', 'Currency & country')}</div>
      <div class="ct-edit-grid">
        <label class="ct-field"><span class="jrn-lbl">${t('contacts.currency', 'Billing currency')}</span>
          <select class="form-control" .value=${fCurrency} @change=${e => { clearEditErr(); fCurrency.value = e.target.value; }}>
            ${((reference.value || {}).currencies || []).map(cu => html`<option value=${String(cu.id)}>${cu.code}${cu.symbol ? ' · ' + cu.symbol : ''}${cu.id === (reference.value || {}).base_currency_id ? ' · ' + t('contacts.base', 'base') : ''}</option>`)}
          </select>
          <small class="cell-sub">${t('contacts.currency_hint', 'New documents for this contact use this currency; defaults to the company base.')}</small>
        </label>
        <label class="ct-field"><span class="jrn-lbl">${t('contacts.country', 'Country')}</span>
          <select class="form-control" .value=${fCountry} @change=${e => { clearEditErr(); fCountry.value = e.target.value; }}>
            <option value="">${t('contacts.country_none', '— Select country —')}</option>
            ${((reference.value || {}).countries || []).map(co => html`<option value=${String(co.id)}>${co.name}</option>`)}
          </select>
        </label>
      </div>

      <div class="ct-edit-sec">${t('contacts.tax_registration', 'Tax & registration')}</div>
      <div class="ct-edit-grid">
        ${field('vat_no', t('contacts.vat_no', 'VAT no'), t('contacts.eg_vat', 'e.g. 4990001234'))}
        ${field('reg_no', t('contacts.reg_no', 'Reg no'), t('contacts.eg_reg', 'e.g. 2019/123456/07'))}
        ${field('tax_no', t('contacts.tax_no', 'Tax no'), t('contacts.eg_tax', 'e.g. 9012345678'))}
      </div>

      <div class="ct-edit-sec">${t('contacts.bank_details', 'Bank details')}</div>
      <p class="cell-sub ct-bank-hint">${t('contacts.bank_details_hint', "The account this contact is paid from or into — used to build a debit-order or payment file.")}</p>
      <div class="ct-edit-grid ct-edit-grid-2">
        ${field('bank_account_name', t('contacts.bank_account_name', 'Account holder name'), t('contacts.eg_bank_account_name', 'e.g. Blue Widgets (Pty) Ltd'))}
        ${field('bank_name', t('contacts.bank_name', 'Bank'), t('contacts.eg_bank_name', 'e.g. First National Bank'))}
        ${field('bank_account_no', t('contacts.bank_account_no', 'Account number'), t('contacts.eg_bank_account_no', 'e.g. 62000112233'))}
        ${field('bank_branch_code', t('contacts.bank_branch_code', 'Branch code'), t('contacts.eg_bank_branch_code', 'e.g. 250655'))}
        <label class="ct-field"><span class="jrn-lbl">${t('contacts.bank_account_type', 'Account type')}</span>
          <select class="form-control" .value=${f.bank_account_type} @change=${e => { clearEditErr(); f.bank_account_type.value = e.target.value; }}>
            <option value="">${t('contacts.bank_account_type_none', '— Select —')}</option>
            <option value="cheque">${t('contacts.bank_type_cheque', 'Cheque / Current')}</option>
            <option value="savings">${t('contacts.bank_type_savings', 'Savings')}</option>
            <option value="transmission">${t('contacts.bank_type_transmission', 'Transmission')}</option>
          </select>
        </label>
      </div>
      ${() => bankIntlOpen.value
        ? html`<div class="ct-edit-grid ct-edit-grid-2 ct-bank-intl">
            ${field('bank_swift', t('contacts.bank_swift', 'SWIFT / BIC'), t('contacts.eg_bank_swift', 'e.g. FIRNZAJJ'))}
            ${field('bank_iban', t('contacts.bank_iban', 'IBAN'), t('contacts.eg_bank_iban', 'e.g. GB29 NWBK 6016 1331 9268 19'))}
          </div>`
        : html`<button type="button" class="btn-link ct-bank-intl-toggle" @click=${() => { bankIntlOpen.value = true; }}>${icon('plus', {size: 13})} ${t('contacts.add_international_bank', 'Add international (SWIFT / IBAN)')}</button>`}

      <div class="ct-new-actions">
        ${() => editErr.value ? html`<div class="row-err">${editErr}</div>` : ''}
        <span class="jrn-spacer"></span>
        <button type="button" class="btn btn-outline" @click=${() => { editing.value = false; }}>${t('common.cancel', 'Cancel')}</button>
        <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
      </div>
    </div>
  </form>`;

  function field(key, label, placeholder, tp, cls){
    return html`<label class=${'ct-field' + (cls ? ' ' + cls : '')}><span class="jrn-lbl">${label}</span>
      <input class="form-control" type=${tp || 'text'} .value=${f[key]} @input=${e => { clearEditErr(); f[key].value = e.target.value; }} placeholder=${placeholder}></label>`;
  }

  // "New ▾" document dropdown (owner Andre 2026-09-18): Customers → Quote / Invoice / Credit Note,
  // Suppliers → Order / GRV / Supplier Credit Note. Each opens the new-document editor pre-scoped to
  // this contact (?contact=). A contact that is both shows both groups. Segments match DOC_TYPES /
  // the /co/{id}/{seg}/new routes in main.js.
  const NEW_DOCS = {
    customer: [['quotes', 'Quote'], ['invoices', 'Invoice'], ['credit-notes', 'Credit Note']],
    supplier: [['purchase-orders', 'Purchase Order'], ['goods-received', 'Goods Received Voucher'],
      ['supplier-credits', 'Supplier Credit Note', 'doc.supplier_credit_note']],
  };
  function startNewDoc(seg){
    newMenuOpen.value = false;
    navigate('/co/' + companyId + '/' + seg + '/new?contact=' + contactId);
  }
  function newDocDropdown(c){
    const groups = [];
    if(c.is_customer) groups.push(['customer', NEW_DOCS.customer]);
    if(c.is_supplier) groups.push(['supplier', NEW_DOCS.supplier]);
    if(!groups.length) return '';
    return html`<div class="ct-new-dd">
      <button type="button" class="btn btn-sm btn-primary ct-new-btn"
        @click=${() => { newMenuOpen.value = !newMenuOpen.value; }}>${icon('plus', {size: 13})} ${t('contacts.new_doc', 'New')} <span class="ct-new-caret">▾</span></button>
      ${newMenuOpen.value ? html`
        <div class="ct-new-backdrop" @click=${() => { newMenuOpen.value = false; }}></div>
        <div class="ct-new-menu">
          ${groups.map(([kind, items]) => html`
            ${groups.length > 1 ? html`<div class="ct-new-menu-head">${kind === 'customer' ? t('contacts.as_customer', 'As customer') : t('contacts.as_supplier', 'As supplier')}</div>` : ''}
            ${items.map(([seg, label, labelKey]) => html`<button type="button" class="ct-new-item" @click=${() => startNewDoc(seg)}>${labelKey ? t(labelKey, label) : label}</button>`)}
          `)}
        </div>` : ''}
    </div>`;
  }

  const countryName = id => ((reference.value || {}).countries || []).find(co => co.id === Number(id))?.name || '';
  function afield(key, label, placeholder){
    return html`<label class="ct-field"><span class="jrn-lbl">${label}</span>
      <input class="form-control" .value=${aFld[key]} @input=${e => { addrErr.value = ''; aFld[key].value = e.target.value; }} placeholder=${placeholder}></label>`;
  }
  function addressManager(){
    const list = addresses.value, open = addrOpen.value;
    return html`<div class="ct-addr-wrap">
      ${list.length === 0 && !open
        ? html`<div class="muted ct-addr-empty">${t('contacts.no_addresses', 'No addresses yet — add a billing or delivery address.')}</div>`
        : html`<div class="ct-addr-list">${list.map(a => html`
          <div class="ct-addr-card">
            <div class="ct-addr-main">
              <div class="ct-addr-badges">
                ${a.is_default_billing ? html`<span class="ct-addr-badge billing">${t('contacts.billing', 'Billing')}</span>` : ''}
                ${a.is_default_delivery ? html`<span class="ct-addr-badge delivery">${t('contacts.delivery', 'Delivery')}</span>` : ''}
                ${a.label ? html`<span class="ct-addr-label">${a.label}</span>` : ''}
              </div>
              <div class="ct-addr-lines">${[a.line1, a.line2, [a.town, a.postal_code].filter(Boolean).join(' '), a.province, countryName(a.country_id)].filter(Boolean).join(', ') || html`<span class="muted">${t('contacts.addr_blank', '(no street set)')}</span>`}</div>
            </div>
            <div class="ct-addr-actions">
              ${!a.is_default_billing ? html`<button type="button" class="btn-link" @click=${() => setAddrDefault(a, 'billing')}>${t('contacts.set_billing', 'Set billing')}</button>` : ''}
              ${!a.is_default_delivery ? html`<button type="button" class="btn-link" @click=${() => setAddrDefault(a, 'delivery')}>${t('contacts.set_delivery', 'Set delivery')}</button>` : ''}
              <button type="button" class="btn-link" @click=${() => openAddrEdit(a)}>${t('common.edit', 'Edit')}</button>
              <span class="ct-addr-delete">${rowDelete({ armed: addrDelId.value === a.id, busy: addrDelBusy.value && addrDelId.value === a.id,
                error: addrDelId.value === a.id ? addrDelErr.value : '', label: t('common.delete', 'Delete'),
                onArm: () => armAddrDelete(a), onCancel: cancelAddrDelete, onDelete: () => delAddr(a) })}</span>
            </div>
          </div>`)}</div>`}

      ${open ? html`<div class="ct-addr-form">
        <div class="ct-addr-form-head">${addrEditId.value ? t('contacts.edit_address', 'Edit address') : t('contacts.new_address', 'New address')}</div>
        <div class="ct-edit-grid">
          ${afield('label', t('contacts.addr_label', 'Label'), t('contacts.eg_label', 'e.g. Head office'))}
          ${afield('attention', t('contacts.attention', 'Attention'), t('contacts.eg_attention', 'e.g. Accounts dept'))}
          ${afield('line1', t('contacts.street', 'Street'), t('contacts.eg_street', 'e.g. 12 Main Rd'))}
          ${afield('line2', t('contacts.addr_line2', 'Line 2'), t('contacts.eg_line2', 'e.g. Unit 4, Blue Park'))}
          ${afield('town', t('contacts.town', 'Town'), t('contacts.eg_town', 'e.g. Durban'))}
          ${afield('province', t('contacts.province', 'Province'), t('contacts.eg_province', 'e.g. KwaZulu-Natal'))}
          ${afield('postal_code', t('contacts.postal_code', 'Postal code'), t('contacts.eg_postal', 'e.g. 4001'))}
          ${afield('phone', t('contacts.phone', 'Phone'), t('contacts.eg_phone', 'e.g. 031 000 0000'))}
          <label class="ct-field"><span class="jrn-lbl">${t('contacts.country', 'Country')}</span>
            <select class="form-control" .value=${aCountry} @change=${e => { aCountry.value = e.target.value; }}>
              <option value="">${t('contacts.country_none', '— Select country —')}</option>
              ${((reference.value || {}).countries || []).map(co => html`<option value=${String(co.id)}>${co.name}</option>`)}
            </select></label>
        </div>
        <label class="ct-field ct-addr-notes"><span class="jrn-lbl">${t('contacts.addr_notes', 'Notes / delivery instructions')}</span>
          <input class="form-control" .value=${aFld.notes} @input=${e => { aFld.notes.value = e.target.value; }} placeholder=${t('contacts.eg_addr_notes', 'e.g. Deliver to loading bay at rear')}></label>
        <div class="ct-addr-toggles">
          <label class="switch"><input type="checkbox" .checked=${aBilling} @change=${e => { aBilling.value = e.target.checked; }}><span class="switch-track"></span> ${t('contacts.default_billing', 'Default billing address')}</label>
          <label class="switch"><input type="checkbox" .checked=${aDelivery} @change=${e => { aDelivery.value = e.target.checked; }}><span class="switch-track"></span> ${t('contacts.default_delivery', 'Default delivery address')}</label>
        </div>
        ${() => addrErr.value ? html`<div class="row-err">${addrErr}</div>` : ''}
        <div class="ct-addr-form-actions">
          <button type="button" class="btn btn-sm btn-outline" @click=${closeAddr}>${t('common.cancel', 'Cancel')}</button>
          <button type="button" class="btn btn-sm btn-primary" ?disabled=${() => addrBusy.value} @click=${saveAddr}>${() => addrBusy.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </div>
      </div>` : ''}
    </div>`;
  }

  const body = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const r = data.value; if(!r) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    const c = r.contact, aging = r.aging || {}, tb = tab.value, isEdit = editing.value;
    const stmt = r.statement || [], msgs = r.messages || [], docs = r.documents || [];
    const hasEmail = !!(c.email && c.email.trim());
    return html`<div>
      <div class="ct-detail-head mb-2">
        <div class="ct-detail-top">
          <div><h1 class="ct-detail-name">${c.name}${c.is_active ? '' : html`<span class="tag-off">${t('contacts.disabled', 'disabled')}</span>`}</h1>
            <div class="ct-detail-sub mono">${c.code}${c.phone ? ' · ' + c.phone : ''}${c.email ? ' · ' + c.email : ''}</div>
            <div class="ct-tags">
              ${cTags.value.map(tg => html`<span class="ct-tag">${tg.label}<button class="ct-tag-x" title=${t('contacts.remove_tag', 'Remove tag')} @click=${() => removeTag(tg.id)}>✕</button></span>`)}
              <form class="ct-tag-addform" @submit=${addTag}>
                <input class="ct-tag-input" list=${'ct-tag-suggest-' + contactId} autocomplete="off"
                  .value=${newTag} @input=${e => { newTag.value = e.target.value; }}
                  placeholder=${t('contacts.add_tag', '+ add tag')} aria-label=${t('contacts.add_tag_aria', 'Add a tag — type to pick an existing one or create a new one')}>
                <datalist id=${'ct-tag-suggest-' + contactId}>
                  ${companyTags.value
                    .filter(tg => !cTags.value.some(ct => ct.id === tg.id))
                    .map(tg => html`<option value=${tg.label}></option>`)}
                </datalist>
              </form>
            </div></div>
          <div class="ct-detail-bal">${money(aging.balance || 0)}<span class="ct-bal-lbl">${t('common.balance', 'balance')}</span></div>
        </div>
        <div class="ct-actions">
          ${newDocDropdown(c)}
          <button class="btn btn-sm btn-outline" @click=${openHistoryAddNote}
            title=${t('contacts.add_note_hint', 'Add a note or task on this contact')}>${t('hn.add_note', 'Add note')}</button>
          ${isCompanyAdmin(coRole.value) ? html`<button class="btn btn-sm btn-outline" ?disabled=${!hasEmail || stmtBusy.value}
            title=${hasEmail ? t('contacts.email_stmt_hint', 'Email this contact their statement') : t('contacts.no_email_hint', 'No email on file — add one under Edit')}
            @click=${() => hasEmail ? emailStatement() : toast(t('contacts.no_email', 'No email on file for this contact'), 'bad')}>${stmtBusy.value ? t('common.sending', 'Sending…') : t('contacts.email_statement', 'Email Statement')}</button>` : ''}
          <button class="btn btn-sm btn-outline" data-statement-pdf ?disabled=${stmtPdfBusy.value}
            title=${t('contacts.statement_pdf_hint', 'Download this contact statement as a PDF')}
            @click=${downloadStatementPdf}>${stmtPdfBusy.value ? t('common.downloading', 'Downloading…') : t('contacts.statement_pdf', 'Statement (PDF)')}</button>
          <button class="btn btn-sm btn-outline" title=${t('contacts.onboard_hint', 'Send the contact a link to complete their own details')}
            @click=${() => { onboardLink.value = ''; onboardEmail = c.email || ''; onboardOpen.value = !onboardOpen.value; }}>${t('contacts.onboard', 'Onboard')}</button>
          <button class="btn btn-sm btn-primary" @click=${() => { editErr.value = ''; editing.value = !isEdit; }}>${isEdit ? t('common.close', 'Close') : t('common.edit', 'Edit')}</button>
        </div>
        ${onboardOpen.value ? html`<div class="ct-onboard">
          <div class="ct-onboard-title">${t('contacts.onboard', 'Onboard')} ${c.name}</div>
          <p class="cb-panel-hint">${t('contacts.onboard_intro', 'Send a secure link — the contact completes their own address, registration and banking details on a public page. The link works for 7 days.')}</p>
          ${onboardLink.value ? html`<div class="ct-onboard-linkrow">
            <input class="form-control mono" readonly .value=${onboardLink.value} aria-label="Onboarding link"
              @focus=${e => e.target.select()}>
            <button class="btn btn-sm btn-outline" @click=${copyOnboardLink}>⧉ ${t('common.copy', 'Copy')}</button>
          </div>` : html`<button class="btn btn-sm btn-primary" ?disabled=${onboardBusy.value}
            @click=${generateOnboardLink}>${onboardBusy.value ? t('contacts.generating', 'Generating…') : t('contacts.generate_link', 'Generate link')}</button>`}
          ${isCompanyAdmin(coRole.value) ? html`<div class="ct-onboard-emailrow">
            <input class="form-control" type="email" placeholder=${t('contacts.email_link_ph', 'Email the link to… e.g. jane@blue.co.za')}
              .value=${onboardEmail} @input=${e => { onboardEmail = e.target.value; }} aria-label="Email the link to">
            <button class="btn btn-sm btn-primary" ?disabled=${onboardBusy.value}
              @click=${emailOnboardLink}>${onboardBusy.value ? t('common.sending', 'Sending…') : t('contacts.email_link', 'Email link')}</button>
          </div>` : ''}
        </div>` : ''}
      </div>

      ${isEdit ? editForm(c) : html`
        ${detailAging(aging)}
        <div class="cb-tabs ct-tabs">
          <button class=${'cb-tab' + (tb === 'statement' ? ' active' : '')} @click=${() => { tab.value = 'statement'; dpane(); }}>${t('contacts.statement', 'Statement')} (${stmt.length})</button>
          <button class=${'cb-tab' + (tb === 'documents' ? ' active' : '')} @click=${() => { tab.value = 'documents'; dpane(); }}>${t('contacts.documents', 'Documents')} (${docs.length})</button>
          <button class=${'cb-tab' + (tb === 'comms' ? ' active' : '')} @click=${() => { tab.value = 'comms'; dpane(); }}>${t('contacts.communications', 'Communications')} (${msgs.length})</button>
          <button class=${'cb-tab' + (tb === 'attachments' ? ' active' : '')} @click=${() => { tab.value = 'attachments'; dpane(); }}>${t('contacts.attachments', 'Attachments')}</button>
        </div>
        <div class=${dpane.on.value ? 'pane-in' : ''}>
        ${tb === 'statement' ? (stmt.length ? html`<div class="table-wrap"><table class="cb-table">
            <thead><tr><th>${t('common.date', 'Date')}</th><th>${t('common.ref', 'Ref')}</th><th>${t('common.description', 'Description')}</th><th class="text-right">${t('common.debit', 'Debit')}</th><th class="text-right">${t('common.credit', 'Credit')}</th><th class="text-right">${t('common.balance', 'Balance')}</th></tr></thead>
            <tbody>${stmt.map(s => {
              // Ref click-through (v2, finished 2026-09-06): a statement line posted by a document
              // opens that document; payment/allocation lines (no document_id) stay plain text.
              const seg = s.document_id && s.type_code ? SEG[s.type_code] : null;
              return html`<tr>
              <td class="mono cb-td-date">${dmy(s.date)}</td>
              <td class="mono">${seg
                ? html`<button type="button" class="btn-link mono" title=${t('contacts.open_doc', 'Open this document')}
                    @click=${() => navigate('/co/' + companyId + '/' + seg + '/' + s.document_id + '/edit')}>${s.reference}</button>`
                : s.reference}</td>
              <td>${s.description}</td>
              <td class="mono text-right">${s.debit ? fmtR(s.debit) : ''}</td>
              <td class="mono text-right">${s.credit ? fmtR(s.credit) : ''}</td>
              <td class="mono text-right">${fmtR(s.balance)}</td></tr>`;
            })}</tbody>
          </table></div>` : html`<div class="muted-block">${t('contacts.no_stmt', 'No posted transactions yet.')}</div>`)
        : tb === 'documents' ? (docs.length ? html`<div class="table-wrap"><table class="cb-table">
            <thead><tr><th>${t('common.date', 'Date')}</th><th>${t('doc.col_number', 'Number')}</th><th>${t('common.type', 'Type')}</th><th>${t('common.ref', 'Ref')}</th><th class="text-right">${t('doc.col_total', 'Total')}</th><th>${t('common.status', 'Status')}</th></tr></thead>
            <tbody>${docs.map(d => {
              // Every document clicks through to open it (owner 2026-09-06). All contact docs are one
              // of the seven families, so SEG[type_code] always resolves — the whole row is the link.
              const seg = SEG[d.type_code];
              return html`<tr class=${seg ? 'clickable' : ''} title=${seg ? t('contacts.open_doc', 'Open this document') : ''}
                  @click=${() => { if(seg) navigate('/co/' + companyId + '/' + seg + '/' + d.id + '/edit'); }}>
                <td class="mono cb-td-date">${dmy(d.document_date)}</td>
                <td class="mono">${d.document_no || t('status.draft', 'Draft')}</td>
                <td>${documentTypeLabel(d)}</td>
                <td class="mono">${d.reference || ''}</td>
                <td class="mono text-right">${fmtIn(d.total, docCurrency(d, null, baseCurrency()))}</td>
                <td><span class=${statusClass(d.status)}>${statusLabel(d.status)}</span></td></tr>`;
            })}</tbody>
          </table></div>` : html`<div class="muted-block">${t('contacts.no_docs', 'No documents for this contact yet.')}</div>`)
        : tb === 'comms' ? (msgs.length ? html`<div class="table-wrap"><table class="cb-table">
            <thead><tr><th class="cb-td-date">${t('common.date', 'Date')}</th><th>${t('contacts.subject', 'Subject')}</th><th>${t('contacts.kind', 'Kind')}</th><th>${t('contacts.status', 'Status')}</th></tr></thead>
            <tbody>${msgs.map(m => html`<tr>
              <td class="mono cb-td-date">${dmy(m.queued_at)}</td>
              <td>${m.subject}</td>
              <td><span class="cm-kind">${m.kind}</span></td>
              <td><span class=${'cm-st cm-st-' + m.status}>${m.status}</span></td></tr>`)}</tbody>
          </table></div>` : html`<div class="muted-block">${t('contacts.no_comms', 'No emails to this contact yet. Invoices and statements you email will appear here.')}</div>`)
        : attachmentsView()}
        </div>
        ${historyNotesNode}`}
    </div>`;
  };

  return tenantShell(companyId, t('contacts.contact', 'Contact'), html`<div class="page page-wide ct-page">
    ${backChip({ path: '/co/' + companyId + '/contacts', label: t('common.contacts', 'Contacts') })}
    ${body}
  </div>`);
}
